import React, { useState, useEffect, useCallback } from "react";
import { Modal, Button, Badge, Spinner } from "react-bootstrap";
import {
  FaCheckCircle,
  FaTimesCircle,
  FaQuestionCircle,
  FaRedo,
  FaMobileAlt,
} from "react-icons/fa";
import { ShieldCheck, Key, Wifi, Bell, CreditCard, Banknote } from "lucide-react";
import api from "../../api";

// ── helpers ─────────────────────────────────────────────────────────

const timeAgo = (dateStr) => {
  if (!dateStr) return "—";
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs} hr ago`;
  return `${Math.floor(hrs / 24)} day(s) ago`;
};

const purposeLabel = (p) => {
  const map = {
    CustomerCode: "Registration",
    BranchAdded: "Branch Added",
    ResetPassword: "Password Reset",
    CSDResend: "CSD Resend",
    CorporateRegister: "Corporate Reg.",
  };
  return map[p] || p || "Unknown";
};

const fmtOut = (val) => {
  const n = parseFloat(val);
  if (isNaN(n)) return "—";
  return n.toLocaleString("en-LK", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

// ── sub-components ───────────────────────────────────────────────────

const StatusCard = ({ icon, title, ok, unknown, label, children }) => {
  const borderColor = unknown ? "#f59e0b" : ok ? "#22c55e" : "#ef4444";
  const labelColor  = unknown ? "#d97706" : ok ? "#16a34a" : "#dc2626";
  return (
    <div
      style={{
        background: "#fff",
        border: "1px solid #e2e8f0",
        borderLeft: `4px solid ${borderColor}`,
        borderRadius: 10,
        padding: "13px 16px",
      }}
    >
      <div className="d-flex align-items-center gap-2">
        {icon}
        <span style={{ fontSize: "0.82rem", fontWeight: 700, color: "#0f172a" }}>{title}</span>
        <div className="ms-auto d-flex align-items-center gap-2 flex-shrink-0">
          {unknown
            ? <FaQuestionCircle color="#f59e0b" size={13} />
            : ok
            ? <FaCheckCircle color="#22c55e" size={13} />
            : <FaTimesCircle color="#ef4444" size={13} />
          }
          <span style={{ fontSize: "0.76rem", color: labelColor, fontWeight: 600 }}>{label}</span>
        </div>
      </div>
      {children && <div style={{ marginTop: 10 }}>{children}</div>}
    </div>
  );
};

const Field = ({ label, value, mono, muted, children, half = true }) => (
  <div className={half ? "col-6" : "col-12"}>
    <p style={{ fontSize: "0.63rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", color: "#94a3b8", margin: 0 }}>
      {label}
    </p>
    {children || (
      <p style={{ fontSize: "0.8rem", fontWeight: 600, color: muted ? "#94a3b8" : "#334155", margin: 0 }}
         className={mono ? "font-monospace" : ""}>
        {value ?? "—"}
      </p>
    )}
  </div>
);

// ── modal ────────────────────────────────────────────────────────────

const CustomerStatusModal = ({ customerCode, branchCode, outstanding, onClose }) => {
  const [status, setStatus]         = useState(null);
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState("");
  const [resendingReset, setResendingReset] = useState(false);
  const [resendMsgReset, setResendMsgReset] = useState(null);
  const [resendingReg, setResendingReg]     = useState(false);
  const [resendMsgReg, setResendMsgReg]     = useState(null);

  const loadStatus = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await api.get("/api/otp-lookup/status", {
        params: { customerCode, branchCode },
      });
      setStatus(data);
    } catch (err) {
      setError(err.response?.data?.error || "Failed to load customer status.");
    } finally {
      setLoading(false);
    }
  }, [customerCode, branchCode]);

  useEffect(() => { loadStatus(); }, [loadStatus]);

  const handleResendReset = async () => {
    setResendingReset(true);
    setResendMsgReset(null);
    try {
      const { data } = await api.post("/api/otp-lookup/resend-otp", { customerCode, branchCode });
      setResendMsgReset({ ok: true, text: data.message });
      setTimeout(loadStatus, 1200);
    } catch (err) {
      setResendMsgReset({ ok: false, text: err.response?.data?.error || "Failed to send OTP." });
    } finally {
      setResendingReset(false);
    }
  };

  const handleResendReg = async () => {
    setResendingReg(true);
    setResendMsgReg(null);
    try {
      const { data } = await api.post("/api/otp-lookup/regen-reg-otp", { customerCode, branchCode });
      setResendMsgReg({ ok: true, text: data.message });
      setTimeout(loadStatus, 1200);
    } catch (err) {
      setResendMsgReg({ ok: false, text: err.response?.data?.error || "Failed to generate OTP." });
    } finally {
      setResendingReg(false);
    }
  };

  const outVal = parseFloat(outstanding) || 0;
  const outColor = outVal > 0 ? "#dc2626" : outVal < 0 ? "#16a34a" : "#64748b";

  return (
    <Modal show onHide={onClose} size="lg" centered scrollable>
      <Modal.Header
        closeButton
        style={{ background: "#f8fafc", borderBottom: "1px solid #e2e8f0", padding: "14px 20px" }}
      >
        <Modal.Title style={{ fontSize: "0.95rem", fontWeight: 700, color: "#0f172a" }}>
          Customer Status
          <span
            className="font-monospace"
            style={{ fontSize: "0.82rem", fontWeight: 400, color: "#64748b", marginLeft: 10 }}
          >
            {customerCode} / {branchCode}
          </span>
        </Modal.Title>
      </Modal.Header>

      <Modal.Body style={{ background: "#f8fafc", padding: 20 }}>
        {loading && (
          <div className="d-flex align-items-center justify-content-center" style={{ minHeight: 220 }}>
            <Spinner animation="border" variant="primary" />
          </div>
        )}

        {error && !loading && (
          <div className="alert alert-danger" style={{ fontSize: "0.85rem" }}>{error}</div>
        )}

        {status && !loading && (
          <div className="d-flex flex-column gap-3">

            {/* ① Registration */}
            <StatusCard
              icon={<ShieldCheck size={15} color="#2563eb" />}
              title="Registration"
              ok={status.registration.isRegistered}
              label={status.registration.isRegistered ? "Registered in App" : "Not Registered"}
            >
              {status.registration.isRegistered ? (
                <div className="row g-2">
                  <Field label="CusID"      value={status.registration.cusId} />
                  <Field label="Branch"     value={status.registration.branchCode} mono />
                  <Field label="Mobile"     value={status.registration.mobile} mono />
                  <Field label="Registered" value={timeAgo(status.registration.registeredAt)} />
                </div>
              ) : (
                <p className="text-muted mb-0" style={{ fontSize: "0.8rem" }}>
                  This branch has not been registered in the mobile app.
                </p>
              )}
            </StatusCard>

            {/* ② OTP */}
            <StatusCard
              icon={<Key size={15} color="#2563eb" />}
              title="OTP"
              ok={status.otp.hasRecord && !status.otp.isUsed}
              unknown={!status.otp.hasRecord}
              label={
                !status.otp.hasRecord
                  ? "No OTP record found"
                  : status.otp.isUsed
                  ? "OTP generated — already used"
                  : "OTP generated — unused"
              }
            >
              {status.otp.hasRecord && (
                <div className="row g-2 mb-2">
                  <Field label="Generated" value={timeAgo(status.otp.generatedAt)} />
                  <Field label="Purpose"   value={purposeLabel(status.otp.purpose)} />
                  <Field label="OTP Status" half>
                    <Badge
                      bg={status.otp.isUsed ? "secondary" : "success"}
                      className="fw-normal"
                      style={{ fontSize: "0.68rem" }}
                    >
                      {status.otp.isUsed ? "Used" : "Unused"}
                    </Badge>
                  </Field>
                  
                </div>
              )}

              <div style={{ marginTop: 10, paddingTop: 10, borderTop: "1px solid #f1f5f9" }}>
                {status.registration.isRegistered ? (
                  /* Registered → only Password Reset makes sense */
                  <div className="d-flex align-items-center gap-2 flex-wrap">
                    <Button
                      size="sm"
                      variant="outline-primary"
                      onClick={handleResendReset}
                      disabled={resendingReset}
                      style={{ borderRadius: 8, fontSize: "0.75rem", padding: "4px 12px" }}
                    >
                      {resendingReset
                        ? <><Spinner size="sm" className="me-1" animation="border" />Sending…</>
                        : <><FaRedo size={10} className="me-1" />Re-send Password Reset OTP</>
                      }
                    </Button>
                    {resendMsgReset && (
                      <span style={{ fontSize: "0.74rem", color: resendMsgReset.ok ? "#16a34a" : "#dc2626", fontWeight: 600 }}>
                        {resendMsgReset.ok ? "✓" : "✗"} {resendMsgReset.text}
                      </span>
                    )}
                  </div>
                ) : (
                  /* Not registered → only Registration OTP makes sense */
                  <div className="d-flex align-items-center gap-2 flex-wrap">
                    <Button
                      size="sm"
                      variant="outline-success"
                      onClick={handleResendReg}
                      disabled={resendingReg}
                      style={{ borderRadius: 8, fontSize: "0.75rem", padding: "4px 12px" }}
                    >
                      {resendingReg
                        ? <><Spinner size="sm" className="me-1" animation="border" />Generating…</>
                        : <><FaRedo size={10} className="me-1" />Re-generate Registration OTP</>
                      }
                    </Button>
                    {resendMsgReg && (
                      <span style={{ fontSize: "0.74rem", color: resendMsgReg.ok ? "#16a34a" : "#dc2626", fontWeight: 600 }}>
                        {resendMsgReg.ok ? "✓" : "✗"} {resendMsgReg.text}
                      </span>
                    )}
                  </div>
                )}
              </div>
            </StatusCard>

            {/* ③ FCM Token */}
            <StatusCard
              icon={<Wifi size={15} color="#2563eb" />}
              title="Active Devices (FCM Tokens)"
              ok={status.fcm.hasToken}
              unknown={!status.registration.isRegistered}
              label={
                !status.registration.isRegistered
                  ? "Not applicable — not registered"
                  : status.fcm.hasToken
                  ? `${status.fcm.devices.length} device(s) active`
                  : "No active devices"
              }
            >
              {status.fcm.hasToken ? (
                <div className="d-flex flex-column gap-2">
                  {status.fcm.devices.map((d, i) => (
                    <div
                      key={i}
                      className="d-flex align-items-center gap-3"
                      style={{ background: "#f1f5f9", borderRadius: 7, padding: "8px 12px" }}
                    >
                      <FaMobileAlt color="#2563eb" size={14} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div
                          className="font-monospace text-truncate"
                          style={{ fontSize: "0.75rem", color: "#334155" }}
                        >
                          {d.tokenPreview}
                        </div>
                        <div style={{ fontSize: "0.7rem", color: "#94a3b8" }}>
                          {d.deviceType} · Last used: {timeAgo(d.lastUsed)}
                        </div>
                      </div>
                      <Badge bg="success" className="fw-normal flex-shrink-0" style={{ fontSize: "0.65rem" }}>
                        Active
                      </Badge>
                    </div>
                  ))}
                </div>
              ) : status.registration.isRegistered ? (
                <p className="text-muted mb-0" style={{ fontSize: "0.8rem" }}>
                  No FCM token found. Customer may not have logged in yet.
                </p>
              ) : null}
            </StatusCard>

            {/* ④ FCM Notification Delivery */}
            {(() => {
              const last = status.notifications.recentHistory[0] || null;
              return (
                <StatusCard
                  icon={<Bell size={15} color="#2563eb" />}
                  title="Push Notification Delivery"
                  ok={last?.status === "sent"}
                  unknown={!last}
                  label={
                    !last
                      ? "No notifications sent yet"
                      : last.status === "sent"
                      ? "Last notification delivered"
                      : "Last notification failed"
                  }
                >
                  {status.notifications.recentHistory.length > 0 ? (
                    <div className="d-flex flex-column gap-1">
                      {status.notifications.recentHistory.map((n, i) => (
                        <div
                          key={i}
                          className="d-flex align-items-center justify-content-between"
                          style={{ background: "#f1f5f9", borderRadius: 7, padding: "6px 10px", fontSize: "0.78rem" }}
                        >
                          <span
                            className="text-truncate me-2"
                            style={{ color: "#334155", maxWidth: 240 }}
                            title={n.title}
                          >
                            {n.title}
                          </span>
                          <div className="d-flex gap-1 flex-shrink-0">
                            <Badge
                              bg={n.status === "sent" ? "success" : "danger"}
                              className="fw-normal"
                              style={{ fontSize: "0.64rem" }}
                            >
                              {n.status}
                            </Badge>
                            {n.status === "sent" && (
                              <Badge
                                bg={n.readStatus === "Yes" ? "secondary" : "warning"}
                                text={n.readStatus === "Yes" ? undefined : "dark"}
                                className="fw-normal"
                                style={{ fontSize: "0.64rem" }}
                              >
                                {n.readStatus === "Yes" ? "Read" : "Unread"}
                              </Badge>
                            )}
                            <span style={{ fontSize: "0.64rem", color: "#94a3b8", alignSelf: "center" }}>
                              {timeAgo(n.sentAt)}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-muted mb-0" style={{ fontSize: "0.8rem" }}>
                      No push notification history found for this customer.
                    </p>
                  )}
                </StatusCard>
              );
            })()}

            {/* ⑤ Billing Balance */}
            <StatusCard
              icon={<CreditCard size={15} color="#2563eb" />}
              title="Billing Balance"
              ok={outVal <= 0}
              label={
                outVal > 0
                  ? `LKR ${fmtOut(outstanding)} outstanding`
                  : outVal < 0
                  ? `LKR ${fmtOut(Math.abs(outstanding))} credit`
                  : "No outstanding balance"
              }
            >
              <div className="d-flex align-items-baseline gap-2">
                <span
                  className="font-monospace"
                  style={{ fontSize: "1.15rem", fontWeight: 700, color: outColor }}
                >
                  LKR {fmtOut(outstanding)}
                </span>
                <span style={{ fontSize: "0.75rem", color: outColor }}>
                  {outVal > 0 ? "Amount due" : outVal < 0 ? "Credit balance" : "Cleared"}
                </span>
              </div>
            </StatusCard>

            {/* ⑥ Payment History */}
            <StatusCard
              icon={<Banknote size={15} color="#2563eb" />}
              title="App Payments"
              ok={status.payments.hasPayments}
              unknown={!status.payments.hasPayments}
              label={
                status.payments.hasPayments
                  ? `Last: LKR ${fmtOut(status.payments.lastPayment.amount)} — ${timeAgo(status.payments.lastPayment.date)}`
                  : "No payments via app yet"
              }
            >
              {status.payments.hasPayments ? (
                <div className="d-flex flex-column gap-1 mt-1">
                  {status.payments.recent.map((p, i) => (
                    <div
                      key={i}
                      className="d-flex align-items-center justify-content-between"
                      style={{ background: "#f1f5f9", borderRadius: 7, padding: "7px 12px" }}
                    >
                      <div>
                        <span
                          className="font-monospace"
                          style={{ fontSize: "0.85rem", fontWeight: 700, color: "#16a34a" }}
                        >
                          LKR {fmtOut(p.amount)}
                        </span>
                        {i === 0 && (
                          <Badge bg="success" className="fw-normal ms-2" style={{ fontSize: "0.62rem" }}>
                            Latest
                          </Badge>
                        )}
                      </div>
                      <span style={{ fontSize: "0.72rem", color: "#94a3b8" }}>
                        {new Date(p.date).toLocaleDateString("en-LK", {
                          day: "2-digit", month: "short", year: "numeric",
                        })}
                        {" · "}
                        {timeAgo(p.date)}
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-muted mb-0 mt-1" style={{ fontSize: "0.8rem" }}>
                  This customer has not made any payments through the mobile app yet.
                </p>
              )}
            </StatusCard>

          </div>
        )}
      </Modal.Body>

      <Modal.Footer style={{ background: "#f8fafc", borderTop: "1px solid #e2e8f0", padding: "10px 20px" }}>
        <Button
          size="sm"
          variant="outline-secondary"
          onClick={loadStatus}
          disabled={loading}
          style={{ borderRadius: 8, fontSize: "0.8rem" }}
        >
          <FaRedo size={10} className="me-1" /> Refresh
        </Button>
        <Button
          size="sm"
          variant="outline-dark"
          onClick={onClose}
          style={{ borderRadius: 8, fontSize: "0.8rem" }}
        >
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default CustomerStatusModal;
